<template>
  <div class="contact-info-div" :class="{ 'is-tamil': isTamil }">
    <div class="seller-summary">
      <div class="seller-avatar">
        <img
          src="../assets/images/detail-component/profile.svg"
          alt="profile"
          loading="lazy"
        />
      </div>
      <div class="seller-copy">
        <p class="seller-name">{{ displaySellerName }}</p>
        <p class="verified-phone">
          <i class="fa-solid fa-shield-halved" aria-hidden="true"></i>
          <span>{{ labels.verified }}</span>
        </p>
        <p class="contact-note">{{ labels.note }}</p>
      </div>
    </div>

    <div class="contact-panel">
      <p class="contact-panel-title">{{ labels.contactSeller }}</p>
      <div class="contact-action-row">
        <a
          class="contact-action call-action"
          :class="{ 'is-disabled': !hasPhone }"
          :href="telLink"
          :aria-disabled="!hasPhone"
          :tabindex="hasPhone ? 0 : -1"
          @click="guardMissingPhone"
        >
          <i class="fa-solid fa-phone" aria-hidden="true"></i>
          <span>
            <strong>{{ labels.callNow }}</strong>
            <small>{{ labels.clickToCall }}</small>
          </span>
        </a>

        <a
          class="contact-action whatsapp-action"
          :class="{ 'is-disabled': !hasPhone }"
          :href="whatsappLink"
          target="_blank"
          rel="noopener noreferrer"
          :aria-disabled="!hasPhone"
          :tabindex="hasPhone ? 0 : -1"
          @click="guardMissingPhone"
        >
          <i class="fa-brands fa-whatsapp" aria-hidden="true"></i>
          <span>
            <strong>WhatsApp</strong>
            <small>{{ labels.chatOnWhatsApp }}</small>
          </span>
        </a>
      </div>

      <div class="phone-reveal-row" :class="{ 'is-visible': showPhone && hasPhone }">
        <button
          class="show-phone-btn"
          type="button"
          :disabled="!hasPhone"
          @click="togglePhoneVisibility"
        >
          <i :class="phoneVisibilityIconClass" aria-hidden="true"></i>
          <span>{{ showPhone && hasPhone ? formattedPhone : labels.showPhone }}</span>
        </button>
        <button
          v-if="showPhone && hasPhone"
          class="copy-phone-btn"
          type="button"
          :aria-label="labels.copyPhone"
          :title="copyTooltip"
          @click="copyPhoneNumber"
        >
          <i :class="copyIconClass" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <div class="seller-warning">
      <i class="fa-solid fa-shield" aria-hidden="true"></i>
      <span>{{ labels.warning }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "ContactSellerCard",
  props: {
    sellerName: {
      type: String,
      default: "",
    },
    phoneNumber: {
      type: [String, Number],
      default: "",
    },
    selectedLanguage: {
      type: String,
      default: "en",
    },
  },
  data() {
    return {
      showPhone: false,
      didCopyPhone: false,
    };
  },
  computed: {
    isTamil() {
      return this.selectedLanguage !== "en";
    },
    labels() {
      if (this.isTamil) {
        return {
          notMentioned: "குறிப்பிடப்படவில்லை",
          verified: "சரிபார்க்கப்பட்ட தொலைபேசி எண்",
          note: "விலை, இருப்பு மற்றும் விவரங்களுக்கு விற்பனையாளரை தொடர்பு கொள்ளவும்",
          contactSeller: "விற்பனையாளரை தொடர்பு கொள்ள",
          callNow: "இப்போது அழைக்க",
          clickToCall: "அழைக்க கிளிக் செய்யவும்",
          chatOnWhatsApp: "WhatsApp-ல் உரையாடவும்",
          showPhone: "தொலைபேசி எண்ணைக் காண கிளிக் செய்யவும்",
          copyPhone: "தொலைபேசி எண்ணை நகலெடுக்கவும்",
          copied: "நகலெடுக்கப்பட்டது",
          warning:
            "எச்சரிக்கையுடன் செயல்படுங்கள். பரிவர்த்தனைக்கு முன் விற்பனையாளர் தகவலை சரிபார்க்கவும்.",
        };
      }

      return {
        notMentioned: "Not Mentioned",
        verified: "Verified Phone Number",
        note: "Contact seller for price, availability and details",
        contactSeller: "Contact Seller",
        callNow: "Call now",
        clickToCall: "Click to call",
        chatOnWhatsApp: "Chat on WhatsApp",
        showPhone: "Click to show phone number",
        copyPhone: "Copy phone number",
        copied: "Copied",
        warning:
          "Use caution. Verify seller's information before making any transaction.",
      };
    },
    displaySellerName() {
      return this.sellerName || this.labels.notMentioned;
    },
    sanitizedPhone() {
      const digits = String(this.phoneNumber || "").replace(/\D/g, "");

      if (digits.length > 10) {
        return digits.slice(-10);
      }

      return digits;
    },
    hasPhone() {
      return this.sanitizedPhone.length >= 8;
    },
    formattedPhone() {
      return `+91 ${this.sanitizedPhone}`;
    },
    telLink() {
      return this.hasPhone ? `tel:+91${this.sanitizedPhone}` : null;
    },
    whatsappLink() {
      return this.hasPhone ? `https://wa.me/91${this.sanitizedPhone}` : null;
    },
    copyTooltip() {
      return this.didCopyPhone ? this.labels.copied : this.labels.copyPhone;
    },
    copyIconClass() {
      return this.didCopyPhone ? "fa-solid fa-check" : "fa-regular fa-copy";
    },
    phoneVisibilityIconClass() {
      return this.showPhone && this.hasPhone
        ? "fa-solid fa-eye-slash"
        : "fa-solid fa-eye";
    },
  },
  watch: {
    phoneNumber() {
      this.showPhone = false;
      this.didCopyPhone = false;
    },
  },
  methods: {
    guardMissingPhone(event) {
      if (!this.hasPhone) {
        event.preventDefault();
      }
    },
    togglePhoneVisibility() {
      if (!this.hasPhone) {
        return;
      }

      this.showPhone = !this.showPhone;
      this.didCopyPhone = false;
    },
    async copyPhoneNumber() {
      if (!this.hasPhone) {
        return;
      }

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.formattedPhone);
        } else {
          this.copyWithFallback(this.formattedPhone);
        }

        this.didCopyPhone = true;
        window.setTimeout(() => {
          this.didCopyPhone = false;
        }, 1600);
      } catch (error) {
        console.error("Unable to copy phone number:", error);
      }
    },
    copyWithFallback(text) {
      const input = document.createElement("textarea");
      input.value = text;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.opacity = "0";
      input.style.pointerEvents = "none";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    },
  },
};
</script>
